/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, FormGroup, Input, Label } from 'reactstrap';
import { loadCustomMetricGroupsByInitiativeId } from '../../actions/initiative';
import { createCompanyOnboarding } from '../../actions/portfolio-tracker';
import { ROUTES } from '../../constants/routes';
import { isValidEmail } from '../../utils';
import { generateUrl } from '../util';
import PortfolioSelect from './PortfolioSelect';
import './styles.scss'
import {
  getUTCEndOf,
  getMonth,
  getMonthOptions,
  getYearDropdownOptions,
  getYear, setDate
} from '../../utils/date';
import { MetricGroup } from '../../types/metricGroup';
import { isStaff } from '../../selectors/user';
import { CheckboxInput, CheckboxProps } from '@g17eco/molecules/form';
import { useAppDispatch, useAppSelector } from '../../reducers';
import className from 'classnames';
import { DEFAULT_YEAR_RANGE } from '../../constants/date';
import { FeatureStability } from '@g17eco/molecules/feature-stability';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { PACK, SURVEY } from '@constants/terminology';
import { CreateOnboardingData, OnboardingType } from '@g17eco/types/onboarding';

interface UserInvitationProps {
  portfolioId: string;
  handleReload: () => void;
}

interface Form {
  isVerified: boolean;
  isPremium: boolean;
  isPublic: boolean;
  createSurvey: boolean;
}

const CompanyInvitationForm = ({ portfolioId, handleReload }: UserInvitationProps) => {

  const [errorMessage, setMessage] = useState<string | undefined>();
  const [isSaving, setSaving] = useState(false);
  const [selectedOnboardingType, setOnboardingType] = useState(OnboardingType.Default);
  const [email, setEmail] = useState<string | undefined>();
  const [companyName, setCompanyName] = useState<string | undefined>();
  const [year, setYear] = useState<number>(getYear());
  const [month, setMonth] = useState<number>(getMonth());
  const [form, setForm] = useState<Form>({
    isVerified: true,
    isPremium: true,
    isPublic: true,
    createSurvey: true,
  });
  const [metricGroupIds, setMetricGroupId] = useState<string[]>([]);
  const [selectedPortfolioId, setPortfolioId] = useState<string | undefined>();

  const metricGroups: MetricGroup[] = useAppSelector((state) => state.customMetricGroups.loaded ? state.customMetricGroups.data : []);
  const isUserStaff = useAppSelector(isStaff)
  const metricGroupsLoaded = metricGroups.length > 0;

  const history = useHistory();
  const dispatch = useAppDispatch();

  const checkOptions: CheckboxProps = {
    groupCode: 'advancedOptions',
    form,
    options: [
      {
        code: 'isPremium',
        name: 'Set company as premium',
        instructions: 'Company will have premium subscription with 5 country limit',
      },
      {
        code: 'isVerified',
        name: 'Set company as verified',
        instructions: 'Verified by WWG staff',
      },
      {
        code: 'isPublic',
        name: 'Investor visibility',
        instructions: 'Set Company to be visible to investors',
      },
      {
        code: 'createSurvey',
        name: `Should pre-create ${SURVEY.SINGULAR} at the beginning`,
        instructions: `${SURVEY.CAPITALIZED_SINGULAR} will be created when starting the onboarding, instead of creating it when user complete onboarding`,
      },
    ],
    onChange: (update: any) => setForm({ ...form, [update.code]: update.value }),
  }

  const onboardingTypes = useMemo(() => {
    return [
      {
        code: OnboardingType.Default,
        label: 'Default',
        group: OnboardingType.Default,
        description: '(Company is invited with no minimum reporting requirements)'
      },
      {
        code: OnboardingType.CustomMetric,
        label: <>Preconfigured <FeatureStability stability={'internal'} /></>,
        group: OnboardingType.CustomMetric,
        description: `(Company is invited with preconfigured {} scope ${PACK.PLURAL} to be answered)`,
        disabled: !metricGroupsLoaded || !isUserStaff
      },
    ]
  }, [metricGroupsLoaded, isUserStaff])

  useEffect(() => {
    dispatch(loadCustomMetricGroupsByInitiativeId(portfolioId));
  }, [dispatch, portfolioId])

  const handleRadioClick = (e: React.ChangeEvent<any>) => {
    setOnboardingType(e.target.value);
  }

  const handleOnChangeEmail = (e: React.ChangeEvent<any>) => {
    setEmail(e.target.value);
  };

  const handleOnChangeCompanyName = (e: React.ChangeEvent<any>) => {
    setCompanyName(e.target.value);
  };

  const handleSubmit = () => {
    if (!isValidEmail(email)) {
      setMessage('The e-mail you typed is invalid. Please check and try again.');
      return;
    }

    setMessage('');

    // Custom metric must use current portfolioId
    const id = OnboardingType.CustomMetric === selectedOnboardingType ? portfolioId : selectedPortfolioId;
    if (!id) {
      return;
    }

    setSaving(true);

    const data: CreateOnboardingData = {
      name: companyName,
      email,
      onboardingType: selectedOnboardingType
    };

    if (OnboardingType.CustomMetric === selectedOnboardingType) {
      data.customMetricIds = metricGroupIds;
      data.effectiveDate = getUTCEndOf('month', setDate({ year, month }));
      data.isVerified = form.isVerified;
      data.isPremium = form.isPremium;
      data.isPublic = form.isPublic;
      data.createSurvey = form.createSurvey;
    }

    createCompanyOnboarding(id, data)
      .then(() => {
        handleReload();
        setSaving(false);
        const rootUrl = generateUrl(ROUTES.PORTFOLIO_TRACKER_MANAGE_COMPANIES, { portfolioId: id });
        history.push(rootUrl);
      })
      .catch(() => {
        handleReload();
        setSaving(false);
      });
  }

  const validEmail = isValidEmail(email);
  const isEnabled = true;
  const customMetricSubmit = OnboardingType.CustomMetric !== selectedOnboardingType ? Boolean(selectedPortfolioId) : Boolean(metricGroupIds);
  const canSubmit = isEnabled && !isSaving && !errorMessage && validEmail && companyName && customMetricSubmit;

  const monthOptions = getMonthOptions();
  const selectedMonth = monthOptions.find((o: any) => o.value === Number(month))

  const yearOptions = getYearDropdownOptions(DEFAULT_YEAR_RANGE);
  const selectedYear = yearOptions.find((o: any) => o.value === year)

  const metricGroupOptions = metricGroups.map((g) => ({ value: g._id, label: g.groupName }))

  return (
    <>
      {errorMessage && <div className='alert alert-danger mt-4'>{errorMessage}</div>}
      <div className='company-invitation-form-container mt-4'>
        <div className='mt-3'>
          <FormGroup>
            <Input
              valid={!!companyName}
              disabled={isSaving}
              type='email'
              placeholder='Company name'
              defaultValue={companyName}
              onChange={handleOnChangeCompanyName}
            />
          </FormGroup>
          <FormGroup>
            <Input
              valid={validEmail}
              invalid={!!email && !validEmail}
              disabled={isSaving}
              type='email'
              placeholder='E-mail address'
              defaultValue={email}
              onChange={handleOnChangeEmail}
            />
          </FormGroup>
          {OnboardingType.Default === selectedOnboardingType && (
            <FormGroup>
              <PortfolioSelect
                disabled={isSaving}
                placeholder='Select the portfolio to add this company'
                selectedPortfolioId={selectedPortfolioId}
                handleSetPortfolio={setPortfolioId}
              />
            </FormGroup>
          )}
          <div className='mt-3'>
            <p className='text-md text-medium py-1 m-0'>Select onboarding type</p>
            {onboardingTypes.map((type) => (
              <FormGroup
                check
                key={`role_${type.code}`}
                className={className('py-1 m-0', {
                  selected: type.group === selectedOnboardingType,
                  disabled: type.disabled,
                })}
              >
                <Input
                  id={`role_${type.code}`}
                  type='radio'
                  name='role'
                  disabled={!!type.disabled}
                  value={type.group}
                  onClick={handleRadioClick}
                  defaultChecked={type.group === selectedOnboardingType}
                />
                <Label htmlFor={`role_${type.code}`} className='text-sm m-0'>{type.label} {type.description}</Label>
              </FormGroup>
            ))}
          </div>
          {OnboardingType.CustomMetric === selectedOnboardingType && (
            <FormGroup className='border-top mt-3'>
              <div className='row mt-2'>
                <div className='col'>
                  <h5>{SURVEY.CAPITALIZED_SINGULAR} configuration</h5>
                </div>
              </div>
              <div className='row mt-2'>
                <div className='col-12 col-md-6'>
                  <SelectFactory
                    selectType={SelectTypes.SingleSelect}
                    isDisabled={isSaving}
                    placeholder={'Select Month'}
                    onChange={(option: any) => setMonth(option?.value)}
                    value={selectedMonth}
                    isSearchable={true}
                    options={monthOptions}
                  />
                </div>
                <div className='col-12 col-md-6'>
                <SelectFactory
                    selectType={SelectTypes.SingleSelect}
                    isDisabled={isSaving}
                    placeholder={'Select Year'}
                    onChange={(option: any) => setYear(option.value)}
                    value={selectedYear}
                    isSearchable={true}
                    options={yearOptions}
                  />
                </div>
              </div>
              <div className='row mt-2'>
                <div className='col-12 col-md-6 mt-2'>
                  <SelectFactory
                    selectType={SelectTypes.MultipleSelect}
                    placeholder={'Select Custom Metric Group'}
                    isDisabled={isSaving}
                    onChange={(values) => setMetricGroupId(values)}
                    values={metricGroupIds}
                    options={metricGroupOptions}
                  />
                </div>
              </div>
              <div className='row mt-2'>
                <div className='col ml-2'>
                  <CheckboxInput disabled={isSaving} {...checkOptions} />
                </div>
              </div>
            </FormGroup>
          )}
        </div>
        <div className='mt-5 text-right'>
          <Button color='primary' disabled={!canSubmit} onClick={handleSubmit}>
            Invite company
          </Button>
        </div>
      </div>
    </>
  );
}

export default CompanyInvitationForm;
